import { datadogRum } from '@datadog/browser-rum';
import dayjs from 'dayjs';
import jwt_decode from 'jwt-decode';
import utc from 'dayjs/plugin/utc';
import duration from 'dayjs/plugin/duration';

import { AuthState } from './Auth.interface';
import { SOCIAL_LOGIN_PROVIDERS, STAFF_ID } from './constants';

import User from 'modules/Auth/common/User.interface';
import DecodedToken from 'modules/Auth/common/DecodedToken.interface';
import { AUTH0_CALLBACK_PATH, AUTH0_LOGOUT_PATH, isAuth0Configured } from 'modules/Auth/auth0/config';

dayjs.extend(utc);
dayjs.extend(duration);

const PUBLIC_ROUTES: string[] = [
    '/login',
    // Clearing the Django session mid-logout makes this route look unauthenticated.
    // Gating it redirects to /login, which starts a fresh Auth0 authorize before
    // the Auth0 session has been cleared, silently signing the user back in.
    AUTH0_LOGOUT_PATH,
    '/verification-code',
    '/signup',
    '/sign-up',
    '/sign-up/with-password',
    '/forgot-password',
    '/reset/:uid/:token',
    '/disable-reminder',
    // Opened from an email, often in a browser with no session.
    '/confirm-email-change',
    '/bookkeeping/invoicing/client-invoice/:invoiceId',
];

/**
 * From the list of PUBLIC_ROUTES detects if the route
 * is there or not
 * TODO: This should be moved to the router instead of detecting manually
 */
export function isPublicRoute(route: string): boolean {
    const routeParts = route.split('/');
    // Auth0 lands on its callback before a session exists; gating it would bounce the user
    // away mid-redirect and the code exchange would never complete. Without Auth0 there is
    // no callback page, so /callback stays an unknown route.
    const publicRoutes = isAuth0Configured() ? [...PUBLIC_ROUTES, AUTH0_CALLBACK_PATH] : PUBLIC_ROUTES;
    const publicRouteParts = publicRoutes.map((publicRoute) => publicRoute.split('/'));

    return publicRouteParts.some((publicRoutePart) => {
        return publicRoutePart.every((publicRoutePartItem, index) => {
            return publicRoutePartItem === routeParts[index] || publicRoutePartItem.startsWith(':');
        });
    });
}

/**
 * Checks if the route is a funnel route that doesn't require
 * waiting for auth check before rendering
 */
export function isFunnelRoute(route: string): boolean {
    return route === '/start' || route.startsWith('/start/');
}

/**
 * Where a visitor whose session is gone goes from `pathname`. A lead in the funnel gets a magic
 * link to resume with, for the email we know when there is one; everyone else gets /login.
 * Reads the stored email, so call it before the session is cleared.
 */
export function signedOutDestination(pathname: string): string {
    if (!isFunnelRoute(pathname)) {
        return '/login';
    }

    const email = JSON.parse(localStorage.getItem('email') || '""');

    return email ? `/login/auto-magic-link?email=${encodeURIComponent(email)}` : '/login/with-magic-link';
}

export const setLocalStorage = (key: string, value: any) => {
    if (value) {
        localStorage.setItem(key, JSON.stringify(value));
    } else {
        localStorage.removeItem(key);
    }
};

/**
 * Creates the initial state for the reducer taking
 * into account the legacy token
 */
export function buildInitialState() {
    const initialToken = localStorage.getItem('token');
    const initialUser = localStorage.getItem('user');

    let initialState: AuthState;

    try {
        initialState = {
            token: initialToken ? JSON.parse(initialToken) : null,
            user: initialUser ? JSON.parse(initialUser) : null,
            showTimeoutModal: false,
            // Nothing has been restored at boot. Auth0SessionGate sets it once the SDK and the
            // user have both loaded.
            auth0Session: false,
        };
    } catch (e) {
        initialState = {
            token: null,
            user: null,
            showTimeoutModal: false,
            auth0Session: false,
        };

        setLocalStorage('token', null);
        setLocalStorage('user', null);
        setLocalStorage('email', null);
        setLocalStorage('employee', false);
        setLocalStorage('impersonator', false);
        setLocalStorage('loggedin', false);
        setLocalStorage('groups', null);
    }

    return initialState;
}

export const setDebugUsers = (user: User) => {
    const id = user.pk.toString();

    let staff = localStorage.getItem(STAFF_ID);
    if (staff) {
        staff = JSON.parse(staff).toString();
    }
    const impersonating = Boolean(staff);

    const debugUser = {
        id,
        impersonating,
        staff,
    };

    const ddUser = datadogRum.getUser();

    if (!ddUser) {
        datadogRum.setUser(debugUser);
    }

    // set Datadog vars
    Object.entries(debugUser).forEach(([key, value]) => {
        datadogRum.setUserProperty(key, value);
    });
};

export const getDecodedToken = (token: string) => {
    return jwt_decode<DecodedToken>(token);
};

export const isTokenExpired = (token: DecodedToken): boolean => {
    const now = dayjs().unix();
    return token.exp < now;
};

export const lastActiveRoute = {
    set: () => {
        const { href: route, pathname } = window.location;
        if (pathname && isPublicRoute(pathname)) {
            return;
        }
        setLocalStorage('lastActiveRoute', {
            route,
        });
    },
    get: () => {
        return JSON.parse(localStorage.getItem('lastActiveRoute') || '{}');
    },
    clear: () => {
        setLocalStorage('lastActiveRoute', null);
    },
};

export const validateSocialLoginProvider = (provider: string): provider is keyof typeof SOCIAL_LOGIN_PROVIDERS => {
    const allowedProviders = Object.values(SOCIAL_LOGIN_PROVIDERS);

    return allowedProviders.includes(provider as keyof typeof SOCIAL_LOGIN_PROVIDERS);
};

/**
 * Where an employee lands after logging in. A sales-only admin gets the sales portal;
 * anyone with hub access gets the hub.
 */
export const getEmployeeRedirectPath = (user?: User | null): string => {
    const isSalesAdmin = user?.groups?.includes('SalesAdmin') && !user?.groups?.includes('HubAdmin');

    return isSalesAdmin ? '/sales-portal' : '/hub-v2';
};
